import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector, useSelectorWithRef } from '../../redux/hooks'
import planningRecordActions from '../../redux/modules/planningRecord/actions'
import planningRecordSelectors from '../../redux/modules/planningRecord/selectors'
import { useIsMountedRef } from '../shared/useIsMountedRef'
import { useUnsubscribe } from '../shared/useUnsubscribe'
import { PlanningRecord } from '@leadgen/models/client'
import useStableCallback from '../shared/useStableCallback'

export const usePlanningRecord = ({
  planningRecordId,
  onLoadComplete,
}: {
  planningRecordId: string
  onLoadComplete?: (planningRecord: PlanningRecord) => void
}) => {
  const stableOnLoadComplete = useStableCallback((planningRecord: PlanningRecord) => {
    if (onLoadComplete) {
      onLoadComplete(planningRecord)
    }
  })
  const isMountedRef = useIsMountedRef()
  const fetchingPlanningRecordIdRef = useRef<string>()
  const { unsubscribe, registerUnsubscribe } = useUnsubscribe()
  const dispatch = useDispatch()
  const planningRecord = useSelector(
    planningRecordSelectors.getPlanningRecord({ planningRecordId }),
  )
  const [loading, loadingRef] = useSelectorWithRef(planningRecordSelectors.getLoading())
  const [, initialRef] = useSelectorWithRef(planningRecordSelectors.getInitial)
  const error = useSelector(planningRecordSelectors.getError)
  const load = useCallback(() => {
    if (
      fetchingPlanningRecordIdRef.current !== planningRecordId ||
      !loadingRef.current ||
      initialRef.current
    ) {
      fetchingPlanningRecordIdRef.current = planningRecordId
      unsubscribe()
      dispatch(planningRecordActions.loadPlanningRecord({ planningRecordId }))
        .unwrap()
        .then(({ data, unsubscribe: unsubscribeFromPlanningRecord }) => {
          if (fetchingPlanningRecordIdRef.current !== planningRecordId) {
            unsubscribeFromPlanningRecord()
          }
          if (isMountedRef.current === false) {
            if (fetchingPlanningRecordIdRef.current == planningRecordId) {
              unsubscribeFromPlanningRecord()
            }
            unsubscribe()
          } else {
            stableOnLoadComplete(data)
            unsubscribe()
            registerUnsubscribe(unsubscribeFromPlanningRecord)
          }
        })
    }
  }, [
    stableOnLoadComplete,
    dispatch,
    isMountedRef,
    loadingRef,
    initialRef,
    registerUnsubscribe,
    unsubscribe,
    planningRecordId,
  ])
  useEffect(() => {
    load()
    return () => {
      unsubscribe()
    }
  }, [load, unsubscribe])
  return {
    planningRecord,
    load,
    loading,
    error,
  }
}
